import { DataType } from "../../../../../models/queries/queries.models";
import { EditableFieldBinding, GridUtils } from "../../../../components/data-grid/data-grid";
import { BaseGhgVerificationRatingFactorClientModel } from "../../../../../models/ghgs/ghg.models";
import { QueryLookupsWithEF, EntityTypeConfigurationModel } from "../../../../../models/basics/basics.models";
import { GhgRatingFactorClientModel } from "../../../../../models/vessels/vessel.models";

export abstract class BaseGhgFactorsRenderer {

    constructor(protected allFactors: (BaseGhgVerificationRatingFactorClientModel | GhgRatingFactorClientModel)[]) {
    }

    public abstract get orderedFactors(): (BaseGhgVerificationRatingFactorClientModel | GhgRatingFactorClientModel)[];

    public abstract showGroup(group: string);

    public abstract getGroupName(group: string): string;

    public abstract isFactorReadonly(factor: BaseGhgVerificationRatingFactorClientModel | GhgRatingFactorClientModel): boolean;

    public abstract showFactor(factor: BaseGhgVerificationRatingFactorClientModel | GhgRatingFactorClientModel);

    public abstract filtersHtml(factor: (BaseGhgVerificationRatingFactorClientModel | GhgRatingFactorClientModel), propValue: string, isEditable: boolean): string;

    public isGroupVisible(group: string) {
        return this.showGroup(group) && this.allFactors.filter(c => c.group == group).some(c => c.isVisible);
    }

    public getDisplayName(factor: BaseGhgVerificationRatingFactorClientModel | GhgRatingFactorClientModel) {

        if (!factor) {
            return null;
        }
        return factor.displayName;
    }

    public getFactorValue(propName: string) {
        const factor = this.allFactors.filter(x => x.propertyName == propName)[0];

        if (!factor) {
            return true;
        }

        if ('revisedValue' in factor && 'submittedValue' in factor) {
            const value = factor.revisedValue !== null ? factor.revisedValue : factor.submittedValue;
            if (factor.dataType == DataType.Boolean) {
                return (typeof value == "string" && (value == "true" || value == "True")) || value === true;
            }
            return value;
        }
        return true;
    }

    onRevisedValueChanged() {
        this.allFactors.forEach(factor => {
            factor.isVisible = this.showFactor(factor);
        });
    }

    public getControl(data: EditableFieldBinding, isFilter: boolean, tooltip: string = null): string {
        let x: string = "<div></div>";

        const tooltipHtml = tooltip ? `tooltip data-pt-title="${tooltip}" data-pt-position="top-right" data-pt-scheme="black" data-pt-target="#tooltip"` : '';

        const startLabelBinding = (data.dataType == DataType.Default || data.dataType == DataType.Number || data.dataType == DataType.Double) && data.startLabel ? data.startLabel : '';
        const endLabelBinding = (data.dataType == DataType.Default || data.dataType == DataType.Number || data.dataType == DataType.Double) && data.endLabel ? data.endLabel : '';

        const inlineStyles = data.inlineStyles ? `style="${data.inlineStyles}"` : "";
        x = `<div ${tooltipHtml} ${inlineStyles} class="${data.classList}" innerhtml="${startLabelBinding}\${${data.displayValueBinding}}${endLabelBinding}"></div>`;

        const debounce: string = isFilter ? " & debounce:350" : "";
        const isLookup = (Number(data.dataType) === DataType.Lookup || Number(data.dataType) === DataType.HierarchicalLookup);
        const placeholder = data.placeholder ? data.placeholder : '';

        if (data.isEditable && (Number(data.dataType) === DataType.Default)) {
            x = `<text-box class="${data.classList}" type='text' ${tooltipHtml} readonly.bind='!${data.isEditable}' textchange.delegate='${data.buttonDelegate}' placeholder='${placeholder}' value.two-way='${data.binding}${debounce}'></text-box>`;
        } else if (data.isEditable && (Number(data.dataType) === DataType.Number || Number(data.dataType) === DataType.Double)) {
            x = `<text-box class="${data.classList}" type='${Number(data.dataType) === DataType.Number ? 'integer' : 'double'}' ${tooltipHtml} readonly.bind='!${data.isEditable}' textchange.delegate='${data.buttonDelegate}'
                           start-label='${data.startLabel ? data.startLabel : ''}' end-label='${data.endLabel ? data.endLabel : ''}'
                           decimal-places='${data.thirdParameterObj ? data.thirdParameterObj : (isFilter ? 10 : 2)}' allow-negative.bind='true'
                           placeholder='${placeholder}' value.two-way='${data.binding}${debounce}'></text-box>`;
        } else if (Number(data.dataType) === DataType.Boolean) {
            if (isFilter || data.isNullable) {
                x = `<combo-box class="${data.classList}" ${tooltipHtml} item-source.bind="${this.getBoolValues(isFilter && data.isNullable)}" selected-value-path="value" display-member-path="name"
                selected-value.two-way="${data.binding}" show-on-adorner.bind="true" readonly.bind='${!data.isEditable ? "true" : "false"}' selected-item-changed.delegate='${data.buttonDelegate}'></combo-box>`;
            } else {
                x = `<check-box class="${data.classList}" ${tooltipHtml} value='${placeholder}' checked.two-way='${data.binding}' changed.delegate='${data.buttonDelegate}'></check-box>`;
            }
        } else if (Number(data.dataType) === DataType.Radio) {
            x = `<radio-button class="${data.classList} padding-5" ${tooltipHtml} readonly.bind='${!data.isEditable ? "true" : "false"}' checked.two-way='${data.binding}' group.bind='${data.parameter}' changed.delegate='${data.buttonDelegate}'></radio-button>`;
        }
        // else if (Number(data.dataType) === DataType.Boolean && !data.isEditable) {
        //     x = `<check-box class="${data.classList}" ${tooltipHtml} readonly.bind='true' checked.bind='${data.displayValueBinding}' changed.delegate='${data.buttonDelegate}'></check-box>`;
        // } 
        else if (data.isEditable && (Number(data.dataType) === DataType.EntityFinder || (isLookup && data.lookup && QueryLookupsWithEF.filter(x => x.lookup.toLowerCase() == data.lookup.toLowerCase()).length > 0))) {

            let entityTypes = `getEntityFinderTypes(${data.query})`;

            if (isLookup) {
                let uri = `Lookups?lookupValueQuery=${data.lookup}`;

                if (data.secondParameter)
                    uri += `&parameters=${data.secondParameter}`;

                const config: EntityTypeConfigurationModel = {
                    uri: uri,
                    claims: [],
                    excludeInDashboard: () => true,
                    isVisible: true,
                    format: "{0}",
                    properties: "name"
                };

                entityTypes = JSON.stringify([config]);
            }

            x = `<entity-finder-search class="${data.classList}" supported-entity-types.bind='${entityTypes}'
                        item-selected.delegate='${data.buttonDelegate}' selected-entity-id.two-way='${data.binding}' 
                        disable-search-popup.bind="true" allow-selection.bind="true"></entity-finder-search>`;

        } else if (data.isEditable && Number(data.dataType) === DataType.Lookup && !data.isValueCollection) {
            x = `<lookup-control class="${data.classList}" cache-flag-key='ghg' no-item-option-label=' ' selected-value-changed.delegate='${data.buttonDelegate}' ${tooltipHtml} placeholder='${placeholder}' show-on-adorner.bind='true' query.bind='${data.query}' value-converter.bind='${data.valueConverter}' selected-value.two-way='${data.binding}'`;
            if (data.thirdParameterObj)
                x += ` exclude-items-by-id.bind='${data.thirdParameterObj}' `
            x += `${GridUtils.getLookupParametersExpression(data.secondParameter, data.isSecondParameterBindable)}></lookup-control>`;
        } else if (data.isEditable && Number(data.dataType) === DataType.Lookup && data.isValueCollection) {
            x = `<multi-select-lookup-control class="${data.classList}" selected-items-changed.delegate='${data.buttonDelegate}' ${tooltipHtml} placeholder='${placeholder}' show-on-adorner.bind='true' query.bind='${data.query}' value-converter.bind='${data.valueConverter}' selected-item-ids.two-way='${data.binding}'`;
            if (data.thirdParameterObj)
                x += ` exclude-items-by-id.bind='${data.thirdParameterObj}' `
            x += `${GridUtils.getLookupParametersExpression(data.secondParameter, data.isSecondParameterBindable)}></multi-select-lookup-control>`;
        } else if (!data.isEditable && Number(data.dataType) === DataType.Lookup) {
            x = `<lookup-control class="${data.classList}" ${tooltipHtml} query.bind='${data.query}' cache-flag-key='ghg' show-on-adorner.bind='true' value-converter.bind='${data.valueConverter}' selected-value.two-way='${data.binding}' ${GridUtils.getLookupParametersExpression(data.secondParameter, data.isSecondParameterBindable)} readonly.bind="true"></lookup-control>`;
        } else if (Number(data.dataType) === DataType.HierarchicalLookup) {
            x = `<lookup-hierarchy-control class="${data.classList}" ${tooltipHtml} query.bind='${data.query}' selected-value.two-way='${data.binding}' ${GridUtils.getLookupParametersExpression(data.secondParameter, data.isSecondParameterBindable)}  readonly.bind="${!data.isEditable}"`
            if (isFilter || data.thirdParameterObj)
                x += ` is-clear-enabled='${isFilter || data.thirdParameterObj}'`;
            x += `></lookup-hierarchy-control>`;
        } else if (Number(data.dataType) === DataType.Icon) {
            x = `<i class='button-icon material-icons ${data.buttonContent} ${data.classList}' ${tooltipHtml} click.trigger='${data.buttonDelegate}'>${data.buttonContent}</i>`;
        } else if (Number(data.dataType) === DataType.Expander) {
            x = `<i class='button-icon material-icons ${data.expanderContent} ${data.classList}' ${tooltipHtml} click.trigger='${data.buttonDelegate}'>${data.expanderContent}</i>`;
        } else if (Number(data.dataType) === DataType.CustomTemplate) {
            x = `<dynamic-view class='templated-div ${data.classList}' ${tooltipHtml} content.bind='${data.value}' binding-context.bind='${data.parameter}'></dynamic-view>`;
        } else if ((Number(data.dataType) === DataType.Date || Number(data.dataType) === DataType.DateTime)) {
            if (data.isNotRangeDatePicker) {
                x = `<div class="date-range"><date-picker class="${data.classList}" readonly.bind='!${data.isEditable}' has-timezone.bind="false" show-picker-button='false' date.two-way='${data.binding}' ${tooltipHtml} date-changed.delegate='${data.buttonDelegate}' placeholder='${placeholder ? placeholder : 'Date'}' ${GridUtils.getDateFormatExpression(data.dataType, data.secondParameter, data.isSecondParameterBindable)}> </date-picker></div>`;
            } else {
                x = `<div class="date-range"><div ${tooltipHtml}><date-picker show-picker-button='false' date.two-way="${data.binding}" placeholder='From' ${GridUtils.getDateFormatExpression(data.dataType, data.secondParameter, data.isSecondParameterBindable)}></date-picker> <date-picker show-picker-button='false' date.two-way="${data.parameter}" placeholder='To' ${GridUtils.getDateFormatExpression(data.dataType, data.secondParameter, data.isSecondParameterBindable)}></date-picker></div>`;
            }

        } else if (Number(data.dataType) === DataType.Button) {
            x = `<custom-button ${tooltipHtml} class="${data.classList}" disabled.bind="${data.secondParameter}" async-click-with-loading="click.call:${data.buttonDelegate}">${data.buttonContent}</custom-button>`;
        } else if (Number(data.dataType) === DataType.ComboBox) {
            x = `<combo-box ${tooltipHtml} class="${data.classList}" readonly.bind='!${data.isEditable}'
               item-source.bind='${typeof data.parameterObj === "string" ? data.parameterObj : JSON.stringify(data.parameterObj)}'
               item-selected.delegate='${data.buttonDelegate}' selected-value.two-way='${data.binding}'
               display-member-path='${data.secondParameterObj ? data.secondParameterObj : ''}'
               selected-value-path='${data.thirdParameterObj ? data.thirdParameterObj : ''}'></combo-box>`;
        } else if (Number(data.dataType) === DataType.Color && data.isEditable) {
            x = `<color-picker ${tooltipHtml} class="${data.classList}" color.two-way='${data.binding}'></color-picker>`;
        } else if (Number(data.dataType) === DataType.Color && !data.isEditable) {
            x = `<div class="${data.classList}">\${${data.displayValueBinding}}</div>`;
        }

        return x;
    }

    private getBoolValues(addNeverSetEntiry: boolean): string {
        return addNeverSetEntiry ? `[
            { name: '', value: null },
            { name: 'Yes', value: true },
            { name: 'No', value: false }
        ]` :
            `[
            { name: '', value: null },
            { name: 'Yes', value: true },
            { name: 'No', value: false }
        ]`;
    }
}